import { useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { IoIosClose } from 'react-icons/io';
import { toast } from 'react-toastify';

import Box from 'components/ui/box';
import Button from 'components/ui/button';
import CustomSelect from 'components/custom-select';
import { useModal } from 'context/ModalContext';
import { requestTaskDeletion } from 'services/dashboard.service';

import {
  DELETE_REASON_OPTIONS,
  DELETE_SUCCESS_MESSAGE,
  DeleteReasonOption,
  GENERIC_DELETE_ERROR_MESSAGE,
  MAX_OTHER_REASON_LENGTH,
  formatReasonCounter,
  validateDeleteReason,
} from '../delete-rules';

/** Textos homologados com o cliente em 2026-09-25. */
const MODAL_TITLE = 'Deseja excluir a Realização Destaque?';
const WITH_REASON_TEXT =
  'Você pode excluir a Realização Destaque em qualquer etapa de validação. Todas as informações e participantes serão excluídos, além de todos os pontos até aqui recebidos.';
const WITHOUT_REASON_TEXT = 'Todas as informações e participantes serão excluídos.';

const ACTION_BUTTON_CLASS =
  'text-white text-sm inline-flex items-center justify-center font-semibold uppercase rounded py-3 px-4 leading-none border-2 border-slate-blue';

const TOAST_OPTIONS = {
  position: 'top-right' as const,
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

const REASON_OPTIONS = [...DELETE_REASON_OPTIONS];

export function deleteTaskModalId(taskId: string) {
  return `delete-task-${taskId}`;
}

interface Props {
  taskId: string;
  /** Regra do front (`deleteRequiresReason`). O servidor pode exigir o motivo mesmo quando ela diz que não. */
  requiresReason: boolean;
  onDeleted: () => void;
  onRefused: () => void;
}

/**
 * Confirmação da exclusão da Realização Destaque pelo cadastrante. A exclusão é o action_log
 * `task_delete`; quem decide o desfecho é a trigger (spec `exclusao-realizacao-action-log`).
 */
export default function ModalDeleteTask({ taskId, requiresReason, onDeleted, onRefused }: Props) {
  ReactModal.setAppElement('#modal');

  const { openModal, closeModal } = useModal();
  const isOpen = openModal === deleteTaskModalId(taskId);

  const [askReason, setAskReason] = useState(requiresReason);
  const [option, setOption] = useState<DeleteReasonOption | undefined>();
  const [otherText, setOtherText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setAskReason(requiresReason);
    setOption(undefined);
    setOtherText('');
  }, [isOpen, requiresReason]);

  const reason = useMemo(() => validateDeleteReason({ option, text: otherText }), [option, otherText]);
  const canConfirm = !sending && (!askReason || reason.valid);
  const otherInputId = `delete-task-other-${taskId}`;

  function close() {
    if (!sending) closeModal();
  }

  async function handleConfirm() {
    if (!canConfirm) return;

    setSending(true);

    try {
      const result = await requestTaskDeletion(
        askReason ? { taskId, reason: reason.reason, reasonOption: reason.reasonOption } : { taskId },
      );
      setSending(false);

      if (result.ok) {
        toast.success(DELETE_SUCCESS_MESSAGE, TOAST_OPTIONS);
        closeModal();
        onDeleted();
        return;
      }

      toast.error(result.message, TOAST_OPTIONS);

      if (result.status === 'REASON_REQUIRED') {
        setAskReason(true);
        return;
      }

      closeModal();
      onRefused();
    } catch (error) {
      setSending(false);
      toast.error(GENERIC_DELETE_ERROR_MESSAGE, TOAST_OPTIONS);
    }
  }

  if (!isOpen) return null;

  return (
    <ReactModal
      isOpen
      onRequestClose={close}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc
      contentLabel="Excluir Realização Destaque"
      portalClassName="relative z-50"
      overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-60 flex flex-col items-center justify-center w-screen h-screen p-6 overflow-y-auto"
      className="outline-none w-[90%] lg:w-auto lg:max-w-[600px]"
    >
      <Box className="w-full">
        <Button
          color="primary"
          className="absolute -right-5 -top-5 rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center"
          onClick={close}
          disabled={sending}
          aria-label="Fechar modal"
        >
          <IoIosClose color="white" size={40} aria-hidden="true" />
        </Button>

        <div className="w-full lg:w-[536px] flex flex-col items-center justify-center space-y-6">
          <h4 tabIndex={0} className="block text-center text-yellow text-lg font-semibold leading-none">
            {MODAL_TITLE}
          </h4>

          <p tabIndex={0} className="text-alice-blue text-base font-thin block w-full text-center">
            {askReason ? WITH_REASON_TEXT : WITHOUT_REASON_TEXT}
          </p>

          {askReason && (
            <>
              <div className="w-full">
                <label htmlFor={`delete-task-reason-${taskId}`} className="text-white font-thin text-xs leading-none mb-2 block">
                  Motivo
                </label>
                <CustomSelect
                  inputId={`delete-task-reason-${taskId}`}
                  ariaLabel="Motivo da exclusão"
                  className="select-realizacao-destaque"
                  placeholder="Selecione o motivo da exclusão"
                  options={REASON_OPTIONS}
                  value={REASON_OPTIONS.find(item => item.value === option) ?? null}
                  onChange={(selected: any) => setOption(selected?.value)}
                  disabled={sending}
                />
              </div>

              {option === 'outros' && (
                <div className="w-full">
                  <label htmlFor={otherInputId} className="text-white font-thin text-xs leading-none mb-2 block">
                    Descreva o motivo
                  </label>
                  <input
                    id={otherInputId}
                    value={otherText}
                    maxLength={MAX_OTHER_REASON_LENGTH}
                    placeholder="Escreva o motivo"
                    onChange={event => setOtherText(event.target.value)}
                    disabled={sending}
                    aria-describedby={`${otherInputId}-counter`}
                    className="w-full bg-maastricht-blue text-white py-2 px-4 rounded-md leading-none text-sm font-light"
                  />
                  <p
                    id={`${otherInputId}-counter`}
                    aria-live="polite"
                    className={`mt-2 text-xs ${reason.valid ? 'text-azureish-white' : 'text-yellow'}`}
                  >
                    {formatReasonCounter(reason.length)}
                  </p>
                </div>
              )}
            </>
          )}

          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              className={`${ACTION_BUTTON_CLASS} bg-transparent hover:bg-slate-blue/20 ${sending ? 'opacity-60' : ''}`}
              onClick={close}
              disabled={sending}
            >
              CANCELAR
            </button>

            <button
              type="button"
              className={`${ACTION_BUTTON_CLASS} bg-slate-blue ${canConfirm ? '' : 'opacity-60'}`}
              onClick={handleConfirm}
              disabled={!canConfirm}
              aria-busy={sending}
            >
              CONFIRMAR
            </button>
          </div>
        </div>
      </Box>
    </ReactModal>
  );
}
